import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import "./LineChart.css";

function isFiniteNumber(value) {
  if (value === null || value === undefined || value === "") {
    return false;
  }

  return Number.isFinite(Number(value));
}

function useElementSize(ref) {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }

      const nextWidth = Math.round(entry.contentRect.width);
      const nextHeight = Math.round(entry.contentRect.height);
      setSize((current) => {
        if (current.width === nextWidth && current.height === nextHeight) {
          return current;
        }

        return {
          width: nextWidth,
          height: nextHeight
        };
      });
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return size;
}

function buildLinePath(points) {
  if (points.length === 0) {
    return "";
  }

  return points.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`).join(" ");
}

function buildAreaPath(points, baselineY) {
  if (points.length === 0) {
    return "";
  }

  const firstPoint = points[0];
  const lastPoint = points[points.length - 1];
  return `${buildLinePath(points)} L ${lastPoint.x} ${baselineY} L ${firstPoint.x} ${baselineY} Z`;
}

function buildSegments(values, labels, toPoint) {
  const segments = [];
  let currentSegment = [];

  values.forEach((value, index) => {
    if (!isFiniteNumber(value)) {
      if (currentSegment.length > 0) {
        segments.push(currentSegment);
        currentSegment = [];
      }

      return;
    }

    currentSegment.push(toPoint(Number(value), index, labels[index]));
  });

  if (currentSegment.length > 0) {
    segments.push(currentSegment);
  }

  return segments;
}

export function LineChart({
  labels = [],
  series = [],
  valueFormatter = (value) => String(value),
  minPlotHeight = "20rem"
}) {
  const plotRef = useRef(null);
  const { width, height } = useElementSize(plotRef);
  const direction = document?.documentElement?.dir === "rtl" ? "rtl" : "ltr";
  const isRtl = direction === "rtl";
  const chart = useMemo(() => {
    const safeWidth = Math.max(width, 320);
    const safeHeight = Math.max(height, 260);
    const padding = {
      top: 20,
      right: 20,
      bottom: 30,
      left: 20
    };
    const innerWidth = Math.max(safeWidth - padding.left - padding.right, 1);
    const innerHeight = Math.max(safeHeight - padding.top - padding.bottom, 1);
    const allValues = series.flatMap((entry) => entry.values ?? []).filter(isFiniteNumber).map(Number);
    const maxValue = Math.max(...allValues, 0);
    const yMax = maxValue > 0 ? maxValue : 1;
    const xStep = labels.length > 1 ? innerWidth / (labels.length - 1) : 0;

    function valueToY(value) {
      return padding.top + innerHeight - (value / yMax) * innerHeight;
    }

    function indexToX(index) {
      if (isRtl) {
        return padding.left + innerWidth - index * xStep;
      }

      return padding.left + index * xStep;
    }

    function toPoint(value, index, label) {
      return {
        x: indexToX(index),
        y: valueToY(value),
        value,
        label
      };
    }

    return {
      width: safeWidth,
      height: safeHeight,
      plotStartX: padding.left,
      plotEndX: padding.left + innerWidth,
      baselineY: padding.top + innerHeight,
      xLabels: labels.map((label, index) => ({
        key: `${label}-${index}`,
        label,
        x: indexToX(index)
      })),
      yTicks: Array.from({ length: 5 }, (_, index) => {
        const value = (yMax / 4) * index;
        return {
          key: `${value}-${index}`,
          value,
          y: valueToY(value),
          x: isRtl ? safeWidth - 10 : padding.left - 10,
          textAnchor: isRtl ? "start" : "start"
        };
      }).reverse(),
      series: series.map((entry) => {
        const segments = buildSegments(entry.values ?? [], labels, toPoint);
        return {
          ...entry,
          segments,
          areaPaths: entry.area ? segments.map((segment) => buildAreaPath(segment, padding.top + innerHeight)) : [],
          linePaths: segments.map((segment) => buildLinePath(segment))
        };
      })
    };
  }, [height, isRtl, labels, series, width]);

  return (
    <div class="line-chart">
      <div class="line-chart-plot" ref={plotRef} style={{ minHeight: minPlotHeight }}>
        <svg
          class="line-chart-svg"
          width={chart.width}
          height={chart.height}
          viewBox={`0 0 ${chart.width} ${chart.height}`}
        >
          {chart.yTicks.map((tick) => (
            <g key={tick.key}>
              <line
                class="line-chart-grid-line"
                x1={chart.plotStartX}
                y1={tick.y}
                x2={chart.plotEndX}
                y2={tick.y}
              />
              <text
                class="line-chart-axis-label y-axis"
                x={tick.x}
                y={tick.y + 4}
                text-anchor={tick.textAnchor}
              >
                {valueFormatter(tick.value)}
              </text>
            </g>
          ))}

          {chart.series.map((entry) => (
            <g key={entry.key}>
              {entry.areaPaths.map((path, index) => (
                <path
                  key={`${entry.key}-area-${index}`}
                  class={`line-chart-area tone-${entry.tone ?? "accent"}`}
                  d={path}
                />
              ))}
              {entry.linePaths.map((path, index) => (
                <path
                  key={`${entry.key}-line-${index}`}
                  class={`line-chart-line tone-${entry.tone ?? "accent"} ${entry.dashed ? "dashed" : ""}`}
                  d={path}
                />
              ))}
              {entry.segments.flatMap((segment, segmentIndex) => segment.map((point, pointIndex) => (
                <circle
                  key={`${entry.key}-point-${segmentIndex}-${pointIndex}`}
                  class={`line-chart-point tone-${entry.tone ?? "accent"} ${entry.pointStyle === "outline" ? "outline" : ""}`}
                  cx={point.x}
                  cy={point.y}
                  r="3.5"
                >
                  <title>{`${point.label}: ${valueFormatter(point.value)}`}</title>
                </circle>
              )))}
            </g>
          ))}

          {chart.xLabels.map((label) => (
            <text
              key={label.key}
              class="line-chart-axis-label x-axis"
              x={label.x}
              y={chart.height - 8}
              text-anchor="middle"
            >
              {label.label}
            </text>
          ))}
        </svg>
      </div>
    </div>
  );
}
